import {
	type CompositionTemplateEntityType,
	type EditorCompositionResponse,
	type GetEditorEditCompositionEntityEnum,
	type GetEditorNewCompositionEntityEnum,
	type GetEditorReviewCompositionEntityEnum,
	type InvestmentExposureResponse,
	type InvestmentSummary,
	type ListInstrumentsProjectedRequest,
	type ListInstrumentsRequest,
	type ReviewTicker,
	type UserEnhancementExposureColumnOrdering,
	type UserInstrumentClassificationDto,
	type UserInstrumentDto,
	type UserInstrumentsColumnOrdering,
	type VerifyEditorPortfolioEntityEnum,
	CompositionControllerV1ApiFactory,
	EntityEditorControllerApiFactory,
	InstrumentsClassificationsControllerV1ApiFactory,
	InstrumentsCustomizationControllerV3ApiFactory,
	InvestmentControllerV4ApiFactory,
	InvestmentReportsControllerApiFactory,
	PortfolioStudioPreferencesApiFactory,
	StandardInstrumentsControllerApiFactory,
	TickerControllerV1ApiFactory,
} from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import type { Abortable } from "@mdotm/utils/promise";
import { minutes, parallelizeWithAllSettled } from "$root/utils";
import { atom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import * as Immutable from "immutable";
import type { Instrument } from "../instruments-editor/const";
import { previousPosition } from "../instruments-editor/quantity/previous-position";
import { portfolioRepository } from "../portfolio/repository";
import { userClassificationsMap$ } from "./user-classifications";
import { generateUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";

const instrumentsRepositoryListKeys = {
	classificationList: "classificationList",
	classificationInstrumentList: "classificationInstrumentList",
};

const instrumentRepositoryColKeys = {
	columnPreferences: "columnPreferences",
	exposureEnhancementColumnPreferences: "exposureEnhancementColumnPreferences",
};

const {
	newQuery,
	newAction,
	invalidateEntity: _invalidateEntity,
	invalidateLists: _invalidateLists,
	invalidate,
} = repositoryHelperFactory("instruments", {
	listKeys: Object.values(instrumentsRepositoryListKeys),
});

const customizationApi = () => getApiGen(InstrumentsCustomizationControllerV3ApiFactory);
const classificationApi = () => getApiGen(InstrumentsClassificationsControllerV1ApiFactory);
const tickerApi = () => getApiGen(TickerControllerV1ApiFactory);
const portfolioStudioPreferencesApi = () => getApiGen(PortfolioStudioPreferencesApiFactory);
const standardInstrumentsApi = () => getApiGen(StandardInstrumentsControllerApiFactory);
const editorApi = () => getApiGen(EntityEditorControllerApiFactory);
const reportsApi = () => getApiGen(InvestmentReportsControllerApiFactory);
const compositionApi = () => getApiGen(CompositionControllerV1ApiFactory);
const investmentApi = () => getApiGen(InvestmentControllerV4ApiFactory);

export const tickersInfoPoolDefault = Immutable.Map<string, UserInstrumentDto>();
export const tickersInfoPool$ = atom(tickersInfoPoolDefault);

const invalidateEntity = (key: string) => {
	writeAtom(tickersInfoPool$, (cur) => cur.remove(key));
	_invalidateEntity(key);
};
const invalidateLists = () => {
	writeAtom(tickersInfoPool$, (cur) => cur.clear());
	_invalidateLists();
};

export const instrumentsRepository = {
	invalidateEntity,
	invalidateLists,

	userClassifications: newQuery(instrumentsRepositoryListKeys.classificationInstrumentList, tickersToInstrumentMap),

	allUserClassifications: newQuery(instrumentsRepositoryListKeys.classificationList, () =>
		axiosExtract(classificationApi().retrieveAllClassifications()),
	),

	userInstrumentsColumnPreferences: newQuery(instrumentRepositoryColKeys.columnPreferences, () =>
		axiosExtract(portfolioStudioPreferencesApi().getUserInstrumentsColumnPreferences()),
	),

	userEnhancementExposureColumnPreferences: newQuery(
		instrumentRepositoryColKeys.exposureEnhancementColumnPreferences,
		() => axiosExtract(portfolioStudioPreferencesApi().getUserEnhancementExposureColumnOrderingPreferences()),
	),

	tickersInfo: newQuery(
		"tickersInfo",
		(tickerList: Array<string /* tickerId */>) => axiosExtract(tickerApi().getTickerInfo(tickerList)),
		{ keepPreviousData: true },
	),

	riskModelAvailableIndices: newQuery(
		"riskModelAvailableIndices",
		() => axiosExtract(customizationApi().getRiskModelAvailableIndexes()),
		{
			cacheTime: minutes(10),
			staleTime: minutes(10),
		},
	),

	setUserInstrumentsColumnPreferences: newAction(
		async (newPreferences: UserInstrumentsColumnOrdering["userInstrumentsColumnPreferences"]) => {
			await portfolioStudioPreferencesApi().setUserInstrumentsColumnPreferences({
				userInstrumentsColumnPreferences: newPreferences,
			});
			invalidate(instrumentsRepository.userInstrumentsColumnPreferences.partialQueryKey);
			invalidate(instrumentsRepository.userClassifications.partialQueryKey);
		},
	),

	setUserExposureEnhancementColumnPreferences: newAction(
		async (newPreferences: UserEnhancementExposureColumnOrdering["userEnhancementExposureColumnPreferences"]) => {
			await portfolioStudioPreferencesApi().setUserEnhancementExposureColumnOrderingPreferences({
				userEnhancementExposureColumnPreferences: newPreferences,
			});
			invalidate(instrumentsRepository.userEnhancementExposureColumnPreferences.partialQueryKey);
		},
	),

	selectableBaskets: newQuery("selectableBaskets", () => axiosExtract(customizationApi().geSelectableBaskets())),

	standardClassifications: newQuery("retrieveStandardClassification", () =>
		axiosExtract(classificationApi().retrieveStandardClassification()),
	),

	projectedStandardInstruments: newQuery("retrieveProjectedStandardInstruments", (payload: ListInstrumentsRequest) =>
		axiosExtract(standardInstrumentsApi().listAllProjectedInstruments(payload)),
	),
	projectedCustomInstruments: newQuery(
		"retrieveProjectedCustomInstruments",
		(payload: ListInstrumentsProjectedRequest) =>
			axiosExtract(customizationApi().listInstrumentsCustomization(payload)),
	),

	isClassificationNameAvailable: newAction((name: string, opts?: Abortable) =>
		axiosExtract(classificationApi().isClassificationNameAvailable(name, opts)),
	),

	createClassificationAndMakeVisible: newAction(async (columnMetadata: UserInstrumentClassificationDto) => {
		const classification = await axiosExtract(classificationApi().createClassification(columnMetadata));
		writeAtom(userClassificationsMap$, (cls) => cls?.set(classification.classificationUuid!, classification) ?? null);

		const userPreferences = await instrumentsRepository.userInstrumentsColumnPreferences.fetchQuery();
		const newColumnPreferences = userPreferences.userInstrumentsColumnPreferences?.map((preference) =>
			preference.preferenceType?.classificationUuid === classification.classificationUuid
				? {
						...preference,
						enabled: true,
					}
				: preference,
		);

		await instrumentsRepository.setUserInstrumentsColumnPreferences(newColumnPreferences);
		invalidateLists();
		invalidate(instrumentsRepository.userInstrumentsColumnPreferences.fullQueryKey);
	}),

	updateClassification: newAction(
		async (classificationUuid: string, classificationSettings: UserInstrumentClassificationDto) => {
			const classification = await axiosExtract(
				classificationApi().updateClassification(classificationUuid, classificationSettings),
			);
			writeAtom(userClassificationsMap$, (cls) => cls?.set(classificationUuid, classification) ?? null);
			invalidateEntity(classificationUuid);
			return classification;
		},
	),

	verifyTagClassificationUsage: newQuery(
		"verifyTagClassificationUsage",
		(classificationUuid: string, classificationValue: string) =>
			axiosExtract(investmentApi().checkPortfoliosByConstraintsAndValue(classificationUuid, classificationValue)),
	),

	deleteTagClassification: newAction(async (classificationUuid: string, tag: string) => {
		await axiosExtract(classificationApi().deleteClassificationValue(classificationUuid, tag));
		writeAtom(userClassificationsMap$, (cls) => {
			const classification = cls?.get(classificationUuid);
			if (classification) {
				return (
					cls?.set(classificationUuid, {
						...classification,
						options: classification.options?.filter((opt) => opt.option !== tag),
					}) ?? null
				);
			}
			return null;
		});
		invalidateEntity(classificationUuid);
	}),

	renameTagClassification: newAction(async (classificationUuid: string, oldTag: string, newTag: string) => {
		await axiosExtract(classificationApi().renameClassificationValue(classificationUuid, oldTag, newTag));
		writeAtom(userClassificationsMap$, (cls) => {
			const classification = cls?.get(classificationUuid);
			if (classification) {
				return (
					cls?.set(classificationUuid, {
						...classification,
						options: classification.options?.map((opt) => {
							if (opt.option !== oldTag || !opt) {
								return opt;
							}

							return { ...opt, option: newTag };
						}),
					}) ?? null
				);
			}
			return null;
		});
		invalidateEntity(classificationUuid);
	}),

	checkColumnInvestments: newQuery("checkColumnInvestments", (payload: string) =>
		axiosExtract(investmentApi().checkPortfoliosByConstraints(payload)),
	),

	disableClassification: newAction(async (classificationUuid: string) => {
		await axiosExtract(classificationApi().disableClassification(classificationUuid));

		writeAtom(userClassificationsMap$, (cls) => cls?.remove(classificationUuid) ?? null);

		invalidateLists();
		invalidate(instrumentsRepository.userInstrumentsColumnPreferences.fullQueryKey);
	}),

	deleteClassificationValue: newAction(async (classificationUuid: string, classificationValue: string) => {
		await axiosExtract(classificationApi().deleteClassificationValue(classificationUuid, classificationValue));
	}),

	deleteClassification: newAction(async (classificationUuid: string) => {
		await axiosExtract(classificationApi().deleteClassification(classificationUuid));
		writeAtom(userClassificationsMap$, (cls) => cls?.delete(classificationUuid) ?? null);
		invalidateLists();
	}),

	generateInstrumentSphereCommentary: newAction(async (tickerName: string) => {
		const commentary = await axiosExtract(customizationApi().generateInstrumentSphereCommentary(tickerName));
		invalidateEntity(tickerName);
		trackMixPanelEvent("instrument.entityAction.generateCommentary", { ticker: tickerName });
		return commentary;
	}),

	reviewComposition: newQuery("reviewComposition", (uuid: string, entity: GetEditorReviewCompositionEntityEnum) =>
		axiosExtract(editorApi().getEditorReviewComposition(uuid, entity)),
	),

	editComposition: newQuery("editComposition", (uuid: string, entity: GetEditorEditCompositionEntityEnum) =>
		axiosExtract(editorApi().getEditorEditComposition(uuid, entity)),
	),

	newComposition: newQuery("newComposition", (entity: GetEditorNewCompositionEntityEnum) =>
		axiosExtract(editorApi().getEditorNewComposition(entity)),
	),

	composition: newQuery("composition", (entity: CompositionTemplateEntityType, uuid: string, aggregate: boolean) =>
		axiosExtract(compositionApi().getComposition(entity, uuid, aggregate)),
	),

	copyComposition: newQuery(
		"copyComposition",
		async (
			uuid: string,
			entity: CompositionTemplateEntityType,
		): Promise<{
			cashTicker?: ReviewTicker;
			instruments: Instrument[];
			/** The copied composition as the response gave it, one entry per `instruments` row, in order. */
			composition: ReviewTicker[];
		}> => {
			const compositionResponse = await axiosExtract(compositionApi().getComposition(entity, uuid, true));
			const composition = compositionResponse.composition ?? [];

			const entityTemplateToEditorEntity = {
				INVESTMENT: "INVESTMENT",
				CUSTOM_BENCHMARK: "BENCHMARK",
				REFERENCE_INVESTMENT: "TARGET_INVESTMENT",
				STANDARD_BENCHMARK: "TARGET_INVESTMENT",
			} satisfies Record<CompositionTemplateEntityType, GetEditorEditCompositionEntityEnum>;

			const editorResponse =
				entity === "STANDARD_BENCHMARK"
					? undefined
					: await instrumentsRepository.editComposition.fetchQuery(uuid, entityTemplateToEditorEntity[entity]);

			const cashTicker = editorResponse?.cashTicker;
			const userInstrumentsMap = await reviewTickerToInstrumentsMap(composition);
			const investmentRichInfo = await instrumentsRepository.investmentsRichInfo.fetchQuery(composition);
			const instruments = createInstrumentList({ composition, userInstrumentsMap, investmentData: investmentRichInfo });

			return { cashTicker, instruments, composition };
		},
	),

	verifyInvestmentComposition: newQuery(
		"verifyInvestmentComposition",
		(uuid: string, weight: number, entity: VerifyEditorPortfolioEntityEnum) =>
			axiosExtract(editorApi().verifyEditorPortfolio(uuid, weight, entity)),
	),

	investmentsRichInfo: newQuery(
		"investmentsRichInfo",
		async (composition: ReviewTicker[]): Promise<InvestmentRichInfo> => {
			let investmentSummaryMap = Immutable.Map<string, InvestmentSummary>();
			let investmentExposureMap = Immutable.Map<string, InvestmentExposureResponse>();
			let verifiedInvestmentsMap = Immutable.Map<string, ReviewTicker[]>();

			const investments = composition.flatMap((instrument) => {
				if (instrument.ticker && instrument.proxyOverwriteType === "PORTFOLIO_MIXED") {
					return [{ uuid: instrument.ticker, weight: instrument.weight }];
				}
				return [];
			});

			await parallelizeWithAllSettled(
				investments.map((investment) => async () => {
					const investmentSummary = await portfolioRepository.summary.fetchQuery(investment.uuid, { enhanced: false });
					const exposure = await axiosExtract(
						reportsApi().getTwoLevelsInvestmentExposure(
							investment.uuid,
							investmentSummary.primaryBenchmarkIdentifier!,
							"MACRO_ASSET_CLASS",
						),
					);
					const { removedTickers } = await axiosExtract(
						editorApi().verifyEditorPortfolio(
							investment.uuid,
							investment.weight ?? 0,
							investmentSummary.status === "PROPOSAL_READY" ? "INVESTMENT_ENHANCEMENT" : "INVESTMENT",
						),
					);

					verifiedInvestmentsMap = verifiedInvestmentsMap.set(investment.uuid, removedTickers ?? []);
					investmentExposureMap = investmentExposureMap.set(investment.uuid, exposure);
					investmentSummaryMap = investmentSummaryMap.set(investment.uuid, investmentSummary);
				}),
				{ concurrency: 3 },
			);

			return { investmentSummaryMap, investmentExposureMap, verifiedInvestmentsMap, investments };
		},
	),
} as const;

export async function tickersToInstrumentMap(tickers: string[]): Promise<Immutable.Map<string, UserInstrumentDto>> {
	const tickersInfo = await instrumentsRepository.tickersInfo.fetchQuery(tickers);
	return Immutable.Map<string, UserInstrumentDto>(
		tickersInfo.map((instrument) => [instrument.tickerName!, instrument]),
	);
}

export async function reviewTickerToInstrumentsMap(
	composition: ReviewTicker[],
): Promise<Immutable.Map<string, UserInstrumentDto>> {
	const tickers = composition.map((x) => x.ticker).filter((ticker) => ticker != null);
	const userInstrumentsMap = await tickersToInstrumentMap(tickers);
	return userInstrumentsMap;
}
export type CompositionInRetrievingDataResponse = {
	instruments: Instrument[];
	compositionWithCash: EditorCompositionResponse;
};

export async function getCompositionDataInRetrievingData(
	uuid: string,
	entity: GetEditorReviewCompositionEntityEnum,
): Promise<CompositionInRetrievingDataResponse> {
	const compositionWithCash = await instrumentsRepository.reviewComposition.fetchQuery(uuid, entity);
	const composition = compositionWithCash.composition ?? [];
	const userInstrumentsMap = await reviewTickerToInstrumentsMap(composition);
	const investmentData = await instrumentsRepository.investmentsRichInfo.fetchQuery(composition);

	return {
		instruments: createInstrumentList({ composition, userInstrumentsMap, investmentData }),
		compositionWithCash,
	};
}

export type InvestmentRichInfo = {
	investmentSummaryMap: Immutable.Map<string, InvestmentSummary>;
	investmentExposureMap: Immutable.Map<string, InvestmentExposureResponse>;
	verifiedInvestmentsMap: Immutable.Map<string, ReviewTicker[]>;
	investments: Array<{ uuid: string; weight: number | undefined }>;
};

export function createInstrumentList({
	composition,
	userInstrumentsMap,
	investmentData,
}: {
	composition: ReviewTicker[];
	userInstrumentsMap: Immutable.Map<string, UserInstrumentDto>;
	investmentData?: InvestmentRichInfo;
}): Instrument[] {
	return composition.map((instrument): Instrument => {
		const { description: _description, type: _type, ...instrumentParams } = instrument;
		const userInstrument = userInstrumentsMap.get(instrument.ticker ?? "") ?? {};
		const tickerName = userInstrument.tickerName ?? instrument.ticker;

		if (userInstrument.proxyOverwriteType === "PORTFOLIO_MIXED") {
			const exposure = investmentData?.investmentExposureMap.get(tickerName ?? "");
			const investmentSummary = investmentData?.investmentSummaryMap.get(tickerName ?? "");
			const someInstrumentAreRemoved = investmentData?.verifiedInvestmentsMap.get(tickerName ?? "") ?? [];

			return {
				...userInstrument,
				tickerName,
				id: tickerName ?? generateUniqueDOMId(),
				name: investmentSummary?.name ?? userInstrument.name ?? instrument.instrument ?? "",
				alias: instrumentParams.alias ?? userInstrument.identifiers?.ALIAS,
				proxyOverwriteType: userInstrument.proxyOverwriteType,
				instrumentType: "INVESTMENT",
				investmentUuid: tickerName ?? investmentSummary!.uuid!,

				nofInstruments: investmentSummary?.nofInstruments,
				macroAssetClassExposure: exposure?.investmentComposition,
				macroAssetClassExposureEnhanced: exposure?.enhancementComposition,
				lastActionNote: investmentSummary?.lastActionNote,
				action: investmentSummary?.action,

				nOfInstrumentExcluded: someInstrumentAreRemoved.length,
				someInstrumentsAreExcluded: someInstrumentAreRemoved.length > 0,

				weight: instrument.weight ?? 0,
				previousWeight: instrument.previousWeight,
				enhancedWeight: instrument.weight ?? 0,
				// Quantity-mode (MDOT-24700) fields, backend-provided — rendered as-is, never computed.
				quantity: instrument.quantity,
				price: instrument.price,
				marketValue: instrument.marketValue,
				...previousPosition(instrument),
				deleted: false,
			};
		}

		return {
			...userInstrument,
			tickerName,
			id: tickerName ?? generateUniqueDOMId(),
			name: userInstrument.name ?? instrument.instrument ?? `Instrument ${instrument.alias}`,
			alias: instrumentParams.alias ?? userInstrument.identifiers?.ALIAS,
			identifiers: {
				...(userInstrument.identifiers ?? {}),
				ALIAS: instrument.alias ?? userInstrument.identifiers?.ALIAS,
			} as Record<string, string>,
			proxyOverwriteType: userInstrument.proxyOverwriteType,
			instrumentType: "INSTRUMENT",

			weight: instrument.weight ?? 0,
			previousWeight: instrument.previousWeight,
			enhancedWeight: instrument.weight ?? 0,
			// Quantity-mode (MDOT-24700) fields, backend-provided — rendered as-is, never computed.
			quantity: instrument.quantity,
			price: instrument.price,
			marketValue: instrument.marketValue,
			...previousPosition(instrument),
			deleted: false,
		};
	});
}
